import React, { useRef, useEffect, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import ScrollContainer from "react-indiana-drag-scroll"
import axios from "axios"
import { useSiteTranslations } from "@hooks/useSiteTranslations"
import { filterProductsByRegexLocale } from "@/utils/filterProductsByLocale"
import { getSetRecommendedProducts } from "@utils/getSetRecommendedProducts"
import { RecommendedProductCard } from "../../main/recommended-product-card/RecommendedProductCard"

import * as s from "./ShopifyGallery.module.scss"

import toBase64String from "../../../utils/toBase64String"

const RecommendedGallery = ({
  className,
  product,
  collection,
  onItemClick,
  pageContext,
  vGrid,
}) => {
  const { allShopifyCollection } = useStaticQuery(graphql`
    query ShopifyCollectionQuery {
      allShopifyCollection {
        nodes {
          products {
            id
            shopifyId
            handle
            title
            tags
            metafields {
              key
              value
            }
            variants {
              id
              shopifyId
              price
              title
              image {
                gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP])
              }
            }
            media {
              ... on ShopifyMediaImage {
                id
                image {
                  src
                  originalSrc
                  gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP])
                }
              }
            }
          }
        }
      }
    }
  `)

  if (!allShopifyCollection) {
    return <></>
  }

  const [products, setProducts] = useState([])
  const {
    shopTranslations: { shop },
  } = useSiteTranslations()

  const fetchData = async (productId, collection) => {
    const shopifyRes = await axios.post(`/.netlify/functions/recommended`, {
      productId: productId,
      collection: collection,
      locale: process.env.GATSBY_LOCALE || "uk",
    })

    return shopifyRes.data
  }

  useEffect(() => {
    if (product) {
      const productId = product.shopifyId || product.id
      const variantId = productId.includes("gid://shopify/")
        ? toBase64String(productId)
        : productId

      const recommendedProducts = async currentProduct => {
        const fetchedProducts = await fetchData(currentProduct, null)

        const filteredProductsByLocale =
          await filterProductsByRegexLocale(fetchedProducts)

        const newProducts = getSetRecommendedProducts(
          allShopifyCollection,
          filteredProductsByLocale,
          pageContext,
        )

        setProducts(newProducts)

        return filteredProductsByLocale
      }

      recommendedProducts(variantId)
    }

    if (collection) {
      const collectionProducts = async collection => {
        const fetchedProducts = await fetchData(null, collection)

        setProducts(fetchedProducts)
      }

      collectionProducts(collection)
    }
  }, [])

  const _scroller = useRef(null)
  const [isScrolling, setIsScrolling] = useState(null)
  const requestRef = useRef(null)

  const handleKeyDown = event => {
    if (!_scroller.current) return

    if (event.keyCode === 37) {
      setIsScrolling(-1)
    } else if (event.keyCode === 39) {
      setIsScrolling(1)
    }
  }

  const step = () => {
    _scroller.current.container.current.scrollLeft =
      _scroller.current.container.current.scrollLeft + isScrolling * 10

    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
  }

  useEffect(() => {
    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(requestRef.current)
  }, [isScrolling])

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const renderMediaItem = (mediaItem, i) => {
    return (
      <RecommendedProductCard
        key={i}
        product={mediaItem}
        onItemClick={onItemClick && onItemClick}
        hasAddToCardButton={false}
      />
    )
  }

  if (!products || products.length < 1) {
    return <></>
  }

  return (
    <div
      className={`${s.GalleryScroll} ${className && className} ${
        vGrid && s.GalleryScroll___vGrid
      } ${collection == "christmas" && s.GalleryScroll___christmas}`}
    >
      <div className={s.GalleryScroll__header}>
        <div className={s.GalleryScroll__header___title}>
          <h4>
            {collection === "christmas"
              ? shop.recommendedProducts.recommendedChristmasTitle
              : shop.recommendedProducts.recommendedTitle}
          </h4>
        </div>
      </div>

      <div className={s.GalleryScroll__inner}>
        <ScrollContainer
          className={s.GalleryScroll__container}
          hideScrollbars={true}
          vertical={false}
          ref={_scroller}
        >
          {products.length >= 1 &&
            products
              .filter(({ variants }) => variants.nodes?.length > 0)
              .map((mediaItem, i) => renderMediaItem(mediaItem, i))}
        </ScrollContainer>
      </div>
    </div>
  )
}

export default RecommendedGallery
